import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, ShieldAlert } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REFERRAL_TYPES = [
  'Family Member', 'Guardian', 'Self Referral', 'Support Coordinator', 'Case Manager',
  'CSB', 'Hospital', 'Behavioral Health Provider', 'Other'
];
const TIMEFRAMES = ['Immediately', '30 Days', '60 Days', '90+ Days', 'Just Exploring'];
const LIVING = ['Family Home', 'Group Home', 'ICF', 'Nursing Facility', 'Hospital', 'Training Center', 'Independent / Apartment', 'Homeless / Unstable Housing', 'Other'];
const WAIVERS = ['Community Living Waiver (CLW)', 'Building Independence Waiver (BI)', 'Family & Individual Supports Waiver (FIS)', 'No Waiver', 'Unknown / Pending', 'Other'];
const FUNDING = ['Medicaid Waiver', 'Private Pay', 'State-Funded', 'Unknown / Pending', 'Other'];
const CONTACT_METHODS = ['Email', 'Phone', 'Either'];

const field = 'mt-2 h-12 rounded-xl border-sage-400 bg-white focus-visible:border-forest-800 focus-visible:ring-2 focus-visible:ring-forest-800/20';
const area = 'mt-2 rounded-xl border-sage-400 bg-white focus-visible:border-forest-800 focus-visible:ring-2 focus-visible:ring-forest-800/20';
const lbl = 'text-forest-900 font-medium text-sm';

const empty = {
  referralTypes: [], specificIndividual: 'yes',
  residentName: '', age: '', countyCity: '', currentLivingArrangement: '', primaryDiagnosis: '',
  supportNeeds: '', currentProvider: '', waiverType: '', fundingSource: '', expectedTimeframe: '', aboutPerson: '',
  contactName: '', relationship: '', phone: '', email: '', preferredContactMethod: '',
  additionalNotes: '', acknowledgement: false
};

const Section = ({ title, children }) => (
  <div className="space-y-6">
    <h3 className="font-serif text-2xl text-forest-800 border-b border-sage-200 pb-2">{title}</h3>
    {children}
  </div>
);

const ReferralInterestForm = () => {
  const [data, setData] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleType = (t) =>
    setData((d) => ({ ...d, referralTypes: d.referralTypes.includes(t) ? d.referralTypes.filter((x) => x !== t) : [...d.referralTypes, t] }));

  const submit = async (e) => {
    e.preventDefault();
    if (data.referralTypes.length === 0) { toast.error('Please select at least one referral type.'); return; }
    if (!data.contactName || !data.email) { toast.error('Please add your name and email so we can respond.'); return; }
    if (!data.acknowledgement) { toast.error('Please acknowledge the expression-of-interest notice.'); return; }
    setSubmitting(true);
    try {
      const payload = { ...data, specificIndividual: data.specificIndividual === 'yes' };
      await axios.post(`${BACKEND_URL}/api/referral-interest`, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong', { description: 'Please try again or email us directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className="p-10 md:p-16 bg-cream-100 border border-sage-200 rounded-3xl text-center shadow-[0_8px_30px_rgba(59,77,65,0.06)]" data-testid="referral-confirmation">
        <div className="inline-flex p-4 rounded-full bg-sage-100 text-forest-700 mb-6"><CheckCircle2 className="w-10 h-10" /></div>
        <h2 className="font-serif text-3xl md:text-4xl text-forest-800 mb-4">Thank You</h2>
        <p className="text-forest-700 text-lg leading-relaxed max-w-2xl mx-auto">
          Thank you for contacting Forever Path Living™. Your referral interest has been received. A member of our
          team will review the information and contact you if additional information is needed. We appreciate the
          opportunity to learn about the individual you are supporting.
        </p>
        <p className="font-serif text-xl md:text-2xl text-gold-700 italic mt-8">Because every story deserves a forever path.</p>
        <Button data-testid="referral-submit-another" onClick={() => { setData(empty); setSubmitted(false); }}
          className="mt-10 rounded-full bg-forest-800 hover:bg-forest-900 text-cream-100 px-8 py-6">
          Submit Another Referral Interest
        </Button>
      </Card>
    );
  }

  const showResident = data.specificIndividual === 'yes';

  return (
    <Card className="p-6 md:p-12 bg-cream-100 border border-sage-200 rounded-3xl shadow-[0_8px_30px_rgba(59,77,65,0.06)]" data-testid="referral-interest-form">
      <form onSubmit={submit} className="space-y-10">
        {/* Referral Type */}
        <Section title="Referral Type">
          <div className="grid sm:grid-cols-2 gap-3">
            {REFERRAL_TYPES.map((t) => (
              <label key={t} className="flex items-center gap-3 p-3 rounded-xl border border-sage-200 bg-white cursor-pointer hover:border-sage-400 transition-colors">
                <Checkbox checked={data.referralTypes.includes(t)} onCheckedChange={() => toggleType(t)}
                  data-testid={`reftype-${t.toLowerCase().replace(/[^a-z]/g, '-')}`} />
                <span className="text-sm text-forest-700">{t}</span>
              </label>
            ))}
          </div>
        </Section>

        {/* Specific individual */}
        <Section title="Is a specific individual being referred?">
          <div className="flex flex-col sm:flex-row gap-3">
            {[
              { v: 'yes', label: 'Yes' },
              { v: 'no', label: "No — I'm gathering information for a future referral" }
            ].map((opt) => (
              <button type="button" key={opt.v} onClick={() => set('specificIndividual', opt.v)}
                data-testid={`specific-${opt.v}`}
                className={`flex-1 text-left px-5 py-4 rounded-xl border transition-colors ${data.specificIndividual === opt.v ? 'bg-forest-800 text-cream-100 border-forest-800' : 'bg-white text-forest-700 border-sage-200 hover:border-sage-400'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </Section>

        {/* Prospective resident */}
        {showResident && (
          <Section title="Prospective Resident Information">
            <div className="grid md:grid-cols-2 gap-6">
              <div><Label className={lbl}>Name</Label><Input data-testid="res-name" className={field} value={data.residentName} onChange={(e) => set('residentName', e.target.value)} /></div>
              <div><Label className={lbl}>Age</Label><Input data-testid="res-age" className={field} value={data.age} onChange={(e) => set('age', e.target.value)} /></div>
              <div><Label className={lbl}>County / City</Label><Input data-testid="res-county" className={field} value={data.countyCity} onChange={(e) => set('countyCity', e.target.value)} /></div>
              <div>
                <Label className={lbl}>Current Living Arrangement</Label>
                <Select value={data.currentLivingArrangement} onValueChange={(v) => set('currentLivingArrangement', v)}>
                  <SelectTrigger className={field + ' w-full'} data-testid="res-living"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{LIVING.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className={lbl}>Primary Diagnosis (optional)</Label><Input data-testid="res-diagnosis" className={field} value={data.primaryDiagnosis} onChange={(e) => set('primaryDiagnosis', e.target.value)} /></div>
              <div><Label className={lbl}>Current Provider</Label><Input data-testid="res-provider" className={field} value={data.currentProvider} onChange={(e) => set('currentProvider', e.target.value)} /></div>
              <div>
                <Label className={lbl}>Waiver Type</Label>
                <Select value={data.waiverType} onValueChange={(v) => set('waiverType', v)}>
                  <SelectTrigger className={field + ' w-full'} data-testid="res-waiver"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{WAIVERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className={lbl}>Funding Source</Label>
                <Select value={data.fundingSource} onValueChange={(v) => set('fundingSource', v)}>
                  <SelectTrigger className={field + ' w-full'} data-testid="res-funding"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{FUNDING.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className={lbl}>Support Needs</Label>
              <Textarea data-testid="res-support" rows={3} className={area} value={data.supportNeeds} onChange={(e) => set('supportNeeds', e.target.value)} />
            </div>
            <div>
              <Label className={lbl}>Expected Timeframe</Label>
              <Select value={data.expectedTimeframe} onValueChange={(v) => set('expectedTimeframe', v)}>
                <SelectTrigger className={field + ' w-full'} data-testid="res-timeframe"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{TIMEFRAMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="p-5 rounded-2xl bg-sage-100 border border-sage-200">
              <Label className={lbl}>Tell us about the person.</Label>
              <p className="text-sm text-forest-700/80 mt-1 mb-2 italic">
                Who is this person beyond their diagnosis? What brings them joy? What helps them feel safe? What should we know about them as a person?
              </p>
              <Textarea data-testid="res-about" rows={5} className={area} value={data.aboutPerson} onChange={(e) => set('aboutPerson', e.target.value)} />
            </div>
          </Section>
        )}

        {/* Contact */}
        <Section title="Contact Information">
          <div className="grid md:grid-cols-2 gap-6">
            <div><Label className={lbl}>Name *</Label><Input data-testid="contact-name" className={field} value={data.contactName} onChange={(e) => set('contactName', e.target.value)} /></div>
            <div><Label className={lbl}>Relationship</Label><Input data-testid="contact-relationship" className={field} value={data.relationship} onChange={(e) => set('relationship', e.target.value)} /></div>
            <div><Label className={lbl}>Phone</Label><Input data-testid="contact-phone" type="tel" className={field} value={data.phone} onChange={(e) => set('phone', e.target.value)} /></div>
            <div><Label className={lbl}>Email *</Label><Input data-testid="contact-email" type="email" className={field} value={data.email} onChange={(e) => set('email', e.target.value)} /></div>
          </div>
          <div>
            <Label className={lbl}>Preferred Contact Method</Label>
            <Select value={data.preferredContactMethod} onValueChange={(v) => set('preferredContactMethod', v)}>
              <SelectTrigger className={field + ' w-full md:w-72'} data-testid="contact-method"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{CONTACT_METHODS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </Section>

        {/* Additional notes */}
        <Section title="Additional Notes">
          <Textarea data-testid="additional-notes" rows={4} className={area} value={data.additionalNotes} onChange={(e) => set('additionalNotes', e.target.value)} />
        </Section>

        {/* FPL statement */}
        <div className="p-6 md:p-8 rounded-2xl bg-forest-900 text-sage-100">
          <p className="leading-relaxed mb-5">
            At Forever Path Living™, we believe every individual deserves to be known before they are supported.
          </p>
          <div className="flex flex-wrap gap-2">
            {['History Does Not Define the Person™', 'Trust Before Truth™', 'Surface Independence™', 'Emotional Intelligence Is the Foundation of Support™'].map((p) => (
              <span key={p} className="px-3 py-1.5 rounded-full text-xs font-medium bg-forest-800 text-cream-100 border border-gold-500/25">{p}</span>
            ))}
          </div>
        </div>

        {/* Legal notice */}
        <div className="flex items-start gap-3 p-5 rounded-2xl bg-cream-200 border border-sage-200" data-testid="legal-notice">
          <ShieldAlert className="w-5 h-5 text-forest-700 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-forest-700 leading-relaxed">
            Submission of this form is an expression of interest only. It does not constitute admission, guarantee
            placement, establish residency, or create a contractual relationship. All referrals are reviewed based
            upon licensing status, compatibility, support needs, funding, availability, and the Forever Path Living™
            admission process.
          </p>
        </div>

        <label className="flex items-start gap-3 p-4 rounded-2xl bg-sage-100 border border-sage-200 cursor-pointer">
          <Checkbox checked={data.acknowledgement} onCheckedChange={(c) => set('acknowledgement', !!c)} className="mt-0.5" data-testid="referral-ack" />
          <span className="text-sm text-forest-800 leading-relaxed">
            I understand this submission is an expression of interest only and does not guarantee admission,
            placement, or availability. *
          </span>
        </label>

        <Button type="submit" disabled={submitting} data-testid="referral-submit"
          className="w-full sm:w-auto rounded-full bg-forest-800 hover:bg-forest-900 text-cream-100 px-10 py-6 text-base font-semibold shadow-lg hover:-translate-y-0.5 transition-transform duration-300">
          {submitting ? 'Submitting…' : 'Submit Referral Interest'}
        </Button>
      </form>
    </Card>
  );
};

export default ReferralInterestForm;
